import React, { createContext, useState } from "react";

import { useTooltip, useTooltipInPortal } from "@visx/tooltip";

import ChartTooltip from "./ChartTooltip";

import type { TooltipContextType, TooltipProviderProps } from "./types";
import type { TooltipData } from "../../types";

export const ChartTooltipContext = createContext<TooltipContextType>({
  tooltipOpen: false,
  tooltipData: undefined,
  showTooltip: undefined,
  hideTooltip: undefined,
  containerBounds: { top: 0, right: 0, bottom: 0, left: 0, x: 0, y: 0 },
});

export const ChartTooltipProvider = ({ children }: TooltipProviderProps) => {
  const [component, setComponent] = useState(undefined);

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip<TooltipData>({
    // initial tooltip state
    tooltipOpen: false,
    tooltipLeft: 0,
    tooltipTop: 0,
    tooltipData: undefined,
  });

  const tooltipProps = {
    tooltipTop: tooltipTop,
    tooltipLeft: tooltipLeft,
    tooltipData,
  };

  const { containerRef, containerBounds } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });

  const onShowTooltip = ({
    tooltipLeft,
    tooltipTop,
    tooltipData,
    component,
  }: TooltipData) => {
    setComponent(component);

    showTooltip({ tooltipLeft, tooltipTop, tooltipData });
  };

  const onHideTooltip = () => {
    setComponent(undefined);

    hideTooltip();
  };

  return (
    <ChartTooltipContext.Provider
      value={{
        tooltipOpen,
        tooltipData,
        showTooltip: onShowTooltip,
        hideTooltip: onHideTooltip,
        containerBounds,
      }}
    >
      <div ref={containerRef}>
        {tooltipOpen ? (
          <ChartTooltip
            {...tooltipProps}
            component={component}
          />
        ) : null}

        {children}
      </div>
    </ChartTooltipContext.Provider>
  );
};
